// FIX-CROP-ERRLOG (E1) — the farmer app's boot-error report, shared by the React root error boundary and (as a
// mirror of) the inline script at the top of index.html, which covers everything that happens before any module
// runs. ONE report per event, at most MAX_REPORTS per page load (the inline script keeps the same counter on
// window.__agxlBootReports). Sent with navigator.sendBeacon to the SECURITY DEFINER RPC log_client_boot_error
// (anon key in the query string — a beacon carries no headers), falling back to fetch({ keepalive }).
// Contents: build, url, userAgent, message, stack, source file + line, service-worker controller script URL and
// its build (GET_BUILD when the worker answers), logged-in yes/no (key present, no values), localStorage KEY NAMES
// only, standalone yes/no, time. Never a phone number, name, token or value.

export const MAX_REPORTS = 3;
export const BOOT_REPORT_RPC = 'log_client_boot_error';

export interface BootReport {
  kind: 'error' | 'unhandledrejection' | 'resource' | 'blank' | 'render';
  build: string | null;
  url: string;
  userAgent: string;
  message: string;
  stack: string | null;
  source: string | null;
  line: number | null;
  sw_controller: string | null;
  sw_build: string | null;
  logged_in: boolean | null;
  ls_keys: string[] | null;
  standalone: boolean | null;
  root_children: number;
  at: string;
}

declare global {
  interface Window { __agxlBootReports?: number; __agxlBootReport?: (kind: BootReport['kind'], message: string, stack?: string | null, source?: string | null, line?: number | null) => void; }
}

/** A short, stable code for the screen ("E-3f2a1c"): the farmer reads it out, the log row carries the same message. */
export function errorCode(message: string): string {
  let h = 0;
  for (const ch of message) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return `E-${h.toString(16).padStart(6, '0').slice(-6)}`;
}

export function buildMeta(doc: Document = document): string | null {
  const m = doc.querySelector('meta[name="agxl-version"]') as HTMLMetaElement | null;
  return m?.content || null;
}

export function localStorageKeys(): string[] | null {
  try {
    const out: string[] = [];
    for (let i = 0; i < localStorage.length && out.length < 60; i++) { const k = localStorage.key(i); if (k) out.push(k.slice(0, 64)); }
    return out;
  } catch { return null; }
}

export function isLoggedIn(): boolean | null {
  try { return !!localStorage.getItem('agxl_user'); } catch { return null; }
}

export function isStandalone(): boolean | null {
  try {
    return (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches) || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  } catch { return null; }
}

/** The controlling worker's script URL and its GET_BUILD answer (null when none controls or it does not answer in 1 s). */
export function swInfo(timeoutMs = 1000): Promise<{ url: string | null; build: string | null }> {
  return new Promise((resolve) => {
    const c = typeof navigator !== 'undefined' && navigator.serviceWorker ? navigator.serviceWorker.controller : null;
    if (!c) { resolve({ url: null, build: null }); return; }
    let done = false;
    const finish = (build: string | null) => { if (!done) { done = true; resolve({ url: c.scriptURL, build }); } };
    try {
      const ch = new MessageChannel();
      const t = setTimeout(() => finish(null), timeoutMs);
      ch.port1.onmessage = (e: MessageEvent) => { clearTimeout(t); finish(typeof e.data?.build === 'string' ? e.data.build : null); };
      c.postMessage({ type: 'GET_BUILD' }, [ch.port2]);
    } catch { finish(null); }
  });
}

export function endpoint(): { url: string; key: string } | null {
  const base = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const key = (import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY) as string | undefined;
  if (!base || !key) return null;
  return { url: `${base.replace(/\/$/, '')}/rest/v1/rpc/${BOOT_REPORT_RPC}?apikey=${encodeURIComponent(key)}`, key };
}

/** Send one report (beacon first, keepalive fetch as the fallback). Returns false when the cap is reached or no endpoint. */
export function deliver(report: BootReport, ep = endpoint()): boolean {
  const w = window as Window;
  const n = w.__agxlBootReports ?? 0;
  if (n >= MAX_REPORTS || !ep) return false;
  w.__agxlBootReports = n + 1;
  const body = JSON.stringify({ p_report: report });
  let ok = false;
  try { ok = typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(ep.url, new Blob([body], { type: 'application/json' })); } catch { ok = false; }
  if (!ok) {
    try { void fetch(ep.url, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json', apikey: ep.key }, body }); } catch { /* nothing more to do */ }
  }
  return true;
}

export async function makeReport(kind: BootReport['kind'], message: string, stack?: string | null, source?: string | null, line?: number | null): Promise<BootReport> {
  const sw = await swInfo();
  return {
    kind, build: buildMeta(), url: location.href, userAgent: navigator.userAgent,
    message: String(message || '').slice(0, 1000), stack: stack ? String(stack).slice(0, 4000) : null,
    source: source ? String(source).slice(0, 300) : null, line: typeof line === 'number' ? line : null,
    sw_controller: sw.url, sw_build: sw.build, logged_in: isLoggedIn(), ls_keys: localStorageKeys(), standalone: isStandalone(),
    root_children: document.getElementById('root')?.children.length ?? -1, at: new Date().toISOString(),
  };
}

/** The boundary's entry point: build the report and deliver it (never throws). */
export async function reportBootError(kind: BootReport['kind'], message: string, stack?: string | null, source?: string | null, line?: number | null): Promise<boolean> {
  try { return deliver(await makeReport(kind, message, stack, source, line)); } catch { return false; }
}
